import PAImage from "component/design-system/images/PAImage";
import PAText from "component/design-system/typography/PAText/PAText";
import FaqAccordion from "container/FaqAccordion";
import AffirmLoaderScript from "modules/checkout/s3/AffirmLoaderScript";
import PageContainer from "modules/components/PageContainer";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./financing.module.scss";

const sections = {
  affirm_info: [
    { title: "Shop for Parts", description: "Browse our inventory and add the auto parts you need to your cart. From engine components to body parts, we have it all.", image: "shop" },
    { title: "Choose Affirm at Checkout", description: "Select Affirm as your payment method and choose your preferred payment plan. See your options instantly.", image: "choose" },
    { title: "Complete Your Purchase", description: "Finalize your order and we'll ship your parts right away. Start making periodic payments on your own schedule.", image: "complete" },
  ],
  affirm_benefits: [
    { title: "Split Your Purchase", description: "Break down the cost of essential auto parts and repairs into manageable monthly payments that fit your budget.", image: "financing-benefits-1" },
    { title: "No Hidden Fees", description: "Know exactly what you'll pay with no hidden charges, late fees, or compounding interest. Just simple, transparent pricing.", image: "financing-benefits-2" },
    { title: "Instant Decision", description: "Get a decision in seconds and complete your purchase right away. No waiting around when you need parts fast.", image: "financing-benefits-3" },
    { title: "100% Transparent", description: "See your full payment schedule upfront. The total you see at checkout is the total you'll pay—guaranteed.", image: "financing-benefits-4" },
    { title: "Build Your Credit", description: "Make on-time payments and you may see your credit score improve. We report to credit bureaus to help build your credit history.", image: "financing-benefits-5" },
    { title: "Flexible Terms", description: "Choose a payment plan that works for you. Select from multiple options to find the perfect fit for your situation.", image: "financing-benefits-6" },
  ],
};

function Financing(props: any) {
  const { t } = useTranslation("static-pages");
  const { locale } = useRouter();
  const faqs = props?.actualResponse?.landingPage?.links?.contentIds?.find((id) => id?.url === `${locale === "en" ? "" : "fr-" }financing-affirm`)?.uiDetails?.content?.faqs || [];

  return (
    <PageContainer fullWidth>
      <AffirmLoaderScript />
      <div className={styles.financing_container}>
        <Link
          href={`https://www.affirm.ca/user/signin?locale=${locale}_CA`}
          target="_blank"
          className={styles.affirm_banner_container}
        >
          <picture key="affirm-financing-banner">
            <source
              srcSet={getRelativeImagePath("/cms/pubicImages/images/affirm-banner-static-page-desktop.webp")}
              media="(min-width: 768px)"
            />
            <img
              alt="affirm-financing-banner"
              src={getRelativeImagePath("/cms/pubicImages/images/affirm-banner-static-page-mobile.webp")}
            />
          </picture>
        </Link>

        <div className={`${styles.financing_section} ${styles.financing_affirm}`}>
          <PAText bold className={styles.financing_title}>
            {t("Buying with Affirm is simple")}
          </PAText>
          <PAText className={styles.financing_subtitle}>
            {t("4 interest-free bi-weekly payments for transactions up to $250, or explore payment plans over 3, 6 or 12 months with APR as low as 0%.")}
            <sup>*</sup>
          </PAText>

          <div className={styles.affirm_info_container}>
            {sections.affirm_info.map((info, index) => (
              <div key={info.title} className={styles.affirm_info_section}>
                <PAImage
                  src={getRelativeImagePath(`/cms/pubicImages/images/financing-${info.image}.png`)}
                  alt={t(info.title)}
                  width={45}
                  height={45}
                  className={styles.affirm_info_image}
                />
                <div className={styles.affirm_info_text}>
                  <PAText component="p" className={styles.affirm_step}>{`${index + 1}. ${t(info.title)}`}</PAText>
                  <PAText component="p" className={styles.affirm_step_info}>{t(info.description)}</PAText>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.financing_section} ${styles.financing_benefits}`}>
          <PAText bold className={styles.financing_title}>
            {t("The Benefits of Financing with Affirm")}
          </PAText>

          <PAText className={styles.financing_subtitle}>
            {t("Affirm makes it easy to afford the auto parts and repairs you need without breaking the bank. Here's why thousands of customers trust Affirm for their purchases.")}
          </PAText>
          <div className={styles.affirm_benefits_container}>
            {sections.affirm_benefits.map((benefit) => (
              <div key={benefit.title} className={styles.affirm_benefit_section}>
                <PAImage
                  src={getRelativeImagePath(`/cms/pubicImages/images/${benefit.image}.png`)}
                  alt={t(benefit.title)}
                  width={55}
                  height={55}
                  className={styles.affirm_benefit_image}
                />
                <PAText bold component="p" className={styles.affirm_benefit_title}>{t(benefit.title)}</PAText>
                <PAText component="p" className={styles.affirm_benefit_info}>{t(benefit.description)}</PAText>
              </div>
            ))}
          </div>
        </div>

        <div className={`${styles.financing_section} ${styles.financing_questions}`}>
          <PAText bold className={styles.financing_title}>
            {t("Frequently Asked Questions")}
          </PAText>
          <FaqAccordion faqs={faqs} faqColCount={2} />
        </div>

        <PAText>
          {t("Payment options through Affirm Canada Holdings Ltd. (\"Affirm\"). Your rate will be 0–31.99% APR (where available and subject to provincial regulatory limitations). APR offered is based on creditworthiness and subject to an eligibility check. Not all customers will be eligible for 0% APR. Payment options depend on your purchase amount, may vary by merchant, and may not be available in all provinces/territories. Actual payment option terms will be shown at checkout. A down payment (or a payment due today) may be required. Affirm accepts debit cards and PAD as forms of repayment on payment options. Select payment options may be eligible for repayment in the form of credit cards. Please review the terms and conditions of your credit card when using it as a form of repayment. Sample payment options may be: a $800 purchase could be split into 12 monthly payments of $72.21 at 15% APR, or 4 interest-free payments of $200 every 2 weeks. For more information, please see")}
        </PAText>

        <Link
          href={`https://www.affirm.com/${locale}-ca/how-it-works`}
          target="_blank"
          className="m-l-5"
        >
          <PAText>
            {`https://www.affirm.com/${locale}-ca/how-it-works`}
          </PAText>
        </Link>
        .
      </div>
    </PageContainer>
  );
}

export default Financing;
